import React, { useEffect, useState } from "react"
import styled from "styled-components"
import { theme } from "../../../theme"
import { ChipButton } from "../../atoms/chip"
import { ChipButtonOption } from "./ChipButtonOption"

export type ChipButtonGroupProps<TOptions extends ChipButtonOption[]> = {
  options: TOptions
  className?: string
  disabled?: boolean
  errorMessage?: string
  // If true, only allow one of child values to be selected.
  singleValue?: boolean
  // When singleValue is true this is a single value, it must have reference equality with the options; when false this is an array of selected values.
  value?: string | number | string[] | number[] | null
  // When singleValue is true this is a single value; when false an array of selected values. If no value is selected and singleValue is true the value is null; when false an empty array.
  onChange: (value: any) => void
}

export const ChipButtonGroup = <TOptions extends ChipButtonOption[]>(props: ChipButtonGroupProps<TOptions>) => {
  const { options, disabled, errorMessage, singleValue = false, value, onChange } = props

  const [selectedValue, setSelectedValue] = useState<any>()

  useEffect(() => {
    // If no value by default is selected and singleValue is true defaultValue is null; when false it is an empty array.
    const emptyValue = !!singleValue ? null : []
    const defaultValue = value ? value : emptyValue
    setSelectedValue(defaultValue)
  }, [value])

  const handleChange = (val: string | number, selected: boolean) => {
    let newSelectedValue

    if (!!singleValue) {
      newSelectedValue = !!selected ? val : null
    } else {
      if (!!selected) {
        newSelectedValue = [...selectedValue, val]
      } else {
        newSelectedValue = [...selectedValue.filter((v: string | number) => v !== val)]
      }
    }
    setSelectedValue(newSelectedValue)
    onChange(newSelectedValue)
  }

  const checkIfValueSelected = (val: string | number): boolean => {
    return !!singleValue ? val === selectedValue : selectedValue?.some((v: string | number) => v === val)
  }

  return (
    <Root className={props.className}>
      <ButtonGroup>
        {options.map((option) => (
          <ChipButton
            key={option.value}
            value={option.value}
            label={option.label}
            icon={option.icon}
            active={!!checkIfValueSelected(option.value)}
            onClick={(_, selected) => handleChange(option.value, !!selected)}
            disabled={disabled}
          />
        ))}
      </ButtonGroup>
      {!!errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
    </Root>
  )
}

const Root = styled.div`
  display: flex;
  flex-flow: column;
`

const ButtonGroup = styled.div`
  display: grid;
  grid-gap: ${theme.spacing.l};
  grid-auto-flow: column;
  justify-content: flex-start;
`

const ErrorMessage = styled.div`
  color: ${theme.color.error};
  margin-top: ${theme.spacing.m};
  font-size: ${theme.fontSize.s};
  line-height: ${theme.lineHeight.s};
`
